import { Injectable } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Observable } from "rxjs";
import { ConfirmDialogComponent } from "../../shared/confirm-dialog/confirm-dialog.component";
import { OrganizationSettingService } from "../organization";
import { UsersService } from "../user";
import { ApiAuthMethodsGet200ResponseInner, AuthMethodsService, AuthenticationMethodConfigurationService, AuthenticationTypeService, CredentialResetProxyService, FidoService, HardwareTokenService, UserCredentialService } from "../identity";
import { TranslateService } from "@ngx-translate/core";
import { EnrollmentInputModel } from "../../shared/enrollment/models/enrollmentinput.model";
import { AuthService } from "../../authentication/auth.service";
import { HasPermissionService } from "./has-permission.service";
import { ConstantService, UserRoles } from "./constant.service";
import { ValidationService } from "./validation.service";

@Injectable({
  providedIn: 'root',
})
export class EnrollmentOptionsService {
  constructor(
    private authService: AuthService,
    private permissions: HasPermissionService,
    private constantService: ConstantService,
    private userService: UsersService,
    private organizationSettingService: OrganizationSettingService,
    private authMethodsService: AuthMethodsService,
    private authMethodConfigService: AuthenticationMethodConfigurationService,
    private hardwareTokenService: HardwareTokenService,
    private userCredentialService: UserCredentialService,
    private authTypeService: AuthenticationTypeService,
    private fidoService: FidoService,
    private ngbModal: NgbModal,
    private translateService: TranslateService,
    private validationService: ValidationService,
    private credentialResetProxyService: CredentialResetProxyService

  ) { }

  async createOptions(
    orgId: number,
    orgMnemonic: string,
    userId: number,
    username: string,
    isUserActive: boolean,
    userType: string,
    disablePrimaryFactor: boolean,
    hidePrimaryFactor: boolean,
    allowMultiplePrimaryFactor: boolean,
    hideSecondaryFactor: boolean,
    enforceSecondaryFactor: boolean,
    allowMultipleSecondaryFactor: boolean,
    skipPasswordPresence: boolean,
  isFromCredentialReset: boolean = false): Promise<EnrollmentInputModel> {

    let role = <UserRoles>userType;
    if (!role) {
      role = UserRoles.User;
    }

    let userEmail: string = null;
    let userPhone: string = null;
    let getUserInfoApiCall: Observable<any>;
    if (isFromCredentialReset) {
      getUserInfoApiCall = this.credentialResetProxyService.apiCredentialResetProxyGetBasicUserInfoGet();
    } else {
      getUserInfoApiCall = this.userService.apiUsersGetUserBasicInfoForUserUserNameOrganizationIdGet(username, orgId);
    }
    let data = await getUserInfoApiCall.toPromise();
    userEmail = data.email;
    userPhone = data.cell;

    return {
      state: {
        disablePrimaryFactor: disablePrimaryFactor,
        hidePrimaryFactor: hidePrimaryFactor,
        allowMultiplePrimaryFactor: allowMultiplePrimaryFactor,
        enforceSecondaryFactor: enforceSecondaryFactor,
        hideSecondaryFactor: hideSecondaryFactor,
        allowMultipleSecondaryFactor: allowMultipleSecondaryFactor,
        skipPasswordPresence: skipPasswordPresence,
        allowedTempTypes: []
      },
      style: {
        showHeading: true,
      },
      org: {
        id: orgId,
        mnemonic: orgMnemonic
      },
      user: {
        id: userId,
        username: username,
        type: userType,
        isActive: isUserActive,
        email: userEmail,
        phone: userPhone
      },
      currentUser: {
        id: +this.authService.id,
        username: this.authService.userName,
        type: this.authService.userType,
        hasPermissionForTarget: this.permissions.allowAccessForTarget(role)
      },
      functions: {
        getOrgSettings: this.getOrgSettings.bind(this),
        getConfig: this.getConfig.bind(this),
        getAuthMethods: this.getAuthMethods.bind(this),
        getAuthMethodsForUser: this.getAuthMethodsForUser.bind(this),
        saveAuthMethods: this.saveAuthMethods.bind(this),
        saveAuthMethodsForUser: this.saveAuthMethodsForUser.bind(this),
        getHardwareTokens: this.getHardwareTokens.bind(this),
        fido: {
          getCredentialOptions: this.getCredentialOptions.bind(this),
          identifyToken: this.identifyToken.bind(this)
        },
        softtoken: {
          userEnrolReadyStatus: this.userEnrolReadyStatus.bind(this),
          userSeedUrl: this.userSeedUrl.bind(this)
        }
      },
      validators: {
        regStaticCodeOrPlaceholder: this.validationService.regStaticCodeOrPlaceholder,
        regPhoneNumber: this.validationService.regPhoneNumber,
        regEmail: this.validationService.regEmail,
        softtoken: {
          regAddSpaceCharacterInputThirdParty: this.validationService.regAddSpaceCharacterInputThirdParty,
          regAddSpaceCharacterInput: this.validationService.regAddSpaceCharacterInput,
          regAddSpaceCharacterOutput: this.validationService.regAddSpaceCharacterOutput
        }
      },
      modals: {
        fido: {
          identifyToken: this.identifyFidoModal.bind(this),
          removeToken: this.removeFidoModal.bind(this)
        }
      }
    }
  }

  getOrgSettings(organizationId: number, groupKey: string): Observable<any> {
    return this.organizationSettingService.apiOrganizationSettingGetSettingsOrganizationIdGet(organizationId, groupKey);
  }

  getConfig(orgId: number, userId: number): Observable<any> {
    return this.authMethodConfigService.apiAuthenticationMethodConfigurationForUserOrganizationIdUserIdGet(orgId, userId);
  }

  getAuthMethods(): Observable<any> {
    return this.authMethodsService.apiAuthMethodsGet();
  }

  getAuthMethodsForUser(orgName: string, username: string): Observable<any> {
    return this.authMethodsService.apiAuthMethodsOrganizationMnemonicUserNameGet(orgName, username);
  }

  saveAuthMethods(methods: ApiAuthMethodsGet200ResponseInner[]): Observable<any> {
    return this.authMethodsService.apiAuthMethodsPost(methods);
  }

  saveAuthMethodsForUser(orgName: string, username: string, userId: number, methods: ApiAuthMethodsGet200ResponseInner[]): Observable<any> {
    return this.authMethodsService.apiAuthMethodsOrganizationMnemonicUserNameUserIdPost(orgName, username, userId, methods);
  }

  getHardwareTokens(orgId: number, username: string): Observable<any> {
    return this.hardwareTokenService.apiHardwareTokenGetByUsernameAndUnAssignedOrganizationIdGet(orgId, username);
  }

  doesUserPasswordExist(userId: number): Observable<any> {
    return this.userCredentialService.apiUserCredentialIsUserPasswordExistsUserIdGet(userId);
  }

  getCredentialOptions(orgName: string, username: string, isUsernameless: boolean): Observable<any> {
    return this.fidoService.apiFidoFidoCredentialOptionsOrganizationMnemonicUserNameGet(orgName, username, isUsernameless);
  }

  identifyToken(orgName: string, username: string, credentialId: string): Observable<any> {
    return this.fidoService.apiFidoIdentifyTokenOrganizationMnemonicUserNamePost(orgName, username, credentialId);
  }

  userEnrolReadyStatus(isSoftTokenOnly: boolean): Observable<any> {
    return this.authTypeService.apiAuthenticationTypeUserEnrollReadyStatusGet(isSoftTokenOnly);
  }

  userSeedUrl(isSoftTokenOnly: boolean, isGenericToken: boolean): Observable<any> {
    return this.authTypeService.apiAuthenticationTypeGetUserSeedUrlGet(isSoftTokenOnly, isGenericToken);
  }

  identifyFidoModal(): NgbModalRef {
    const identifyFidoModal = this.ngbModal.open(ConfirmDialogComponent, {
      size: 'md',
      windowClass: 'fadeInUp animated huge',
      backdrop: 'static',
      keyboard: false,
    });
    identifyFidoModal.componentInstance.message = this.translateService.instant('MyProfile.IdentifyTokenMessage');
    return identifyFidoModal;
  }

  removeFidoModal(): NgbModalRef {
    const deleteFidoModal = this.ngbModal.open(ConfirmDialogComponent, {
      size: 'md',
      windowClass: 'fadeInUp animated huge',
      backdrop: 'static',
      keyboard: false,
    });
    deleteFidoModal.componentInstance.message = this.translateService.instant('MyProfile.ConfirmDeleteTokenMessage');
    return deleteFidoModal;
  }

}
